import { useEffect, useMemo, useState } from 'react';
import ImageUploading from 'react-images-uploading';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import DeleteIcon from '@/assets/all-icons/delete-icon';
import config from '@/config';
import useTranslations from '@/i18n/useTranslations';
import PopupUpdateArticleCover from '@/pages/artwork/artwork-header-mobile/options/download-template/components/PopupUpdateArticleCover';
import ChannelsService from '@/services/channels.service';
import SupportService from '@/services/support.service';
import UserService from '@/services/user.service';
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';
import ArtworkImage from '@/shared-components/cards/components/ArtworkImage';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import PasswordEyeOpen from '@/shared-components/icons/components/forms/PasswordEyeOpen';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import PublicFormInput from '@/shared-components/inputs/components/PublicFormInput';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import FHSlider from '@/shared-components/sliders/components/FHSlider';
import { formatCurrencyAmount } from '@/utils/currency.utils';

import AllStickersView from './stickers/AllStickersView';
import CreateStickersButton from './stickers/CreateStickersButton';
import CreateStickersPopup from './stickers/CreateStickersPopup';

import './styles/ChannelSettingsView.css';
import './styles/SubscriptionPreview.css';

const CHANNEL_CURRENCY_OPTIONS = Array.isArray(config?.forms?.currency_options)
  ? config.forms.currency_options
  : ['USD', 'EUR', 'GBP'];
const DEFAULT_CHANNEL_CURRENCY = CHANNEL_CURRENCY_OPTIONS[0] || 'USD';
const WRITE_PERMISSION_OPTIONS = [
  { value: 'everyone', label: 'Everyone (recommended)' },
  { value: 'none', label: 'Myself only' },
];
const SUPPORT_OPTIONS_ACTIVE = [
  { value: 'enabled', label: 'Yes (recommended)' },
  { value: 'disabled', label: 'No' },
];
const SUPPORT_OPTIONS_INACTIVE = [
  { value: 'not_active', label: 'Support not active' },
];

const DEFAULT_DRAFT = {
  title: '',
  type: 'public',
  description: '',
  image: '',
  price: '',
  currency: DEFAULT_CHANNEL_CURRENCY,
  write_perms: 'everyone',
};

function resolveChannelCurrency(rawCurrency) {
  if (typeof rawCurrency !== 'string') return DEFAULT_CHANNEL_CURRENCY;
  const normalized = rawCurrency.trim().toUpperCase();
  return CHANNEL_CURRENCY_OPTIONS.includes(normalized)
    ? normalized
    : DEFAULT_CHANNEL_CURRENCY;
}

function normalizeCoverImageValue(rawImage) {
  if (typeof rawImage !== 'string') return '';
  const normalized = rawImage.trim();
  if (!normalized) return '';
  const lowered = normalized.toLowerCase();
  if (lowered === 'false' || lowered === 'null' || lowered === 'undefined') {
    return '';
  }
  return normalized;
}

function normalizeWritePerms(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'none' ? 'none' : 'everyone';
}

function resolveSupportSelection(channelSupportValue, supportIsActive) {
  if (!supportIsActive) {
    return 'not_active';
  }
  return channelSupportValue === false ? 'disabled' : 'enabled';
}

function resolveSupportPayloadValue(selection, supportIsActive) {
  if (!supportIsActive) {
    return null;
  }
  return selection === 'enabled';
}

function resolveCoverImageSrc(rawImage) {
  const normalized = normalizeCoverImageValue(rawImage);
  if (!normalized) return '';
  if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('blob:')) {
    return normalized;
  }
  return `${config.app.imageServiceDomain}/${normalized}/w=1600`;
}

export default function ChannelSettingsView() {
  const navigate = useNavigate();
  const { user_username } = useParams();
  const location = useLocation();
  const t = useTranslations('common.profile.channels');
  const searchParams = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const channelIdFromState = location.state?.channelId;
  const channelIdFromQuery = searchParams.get('channelId');
  const sourceChannelId = channelIdFromState || channelIdFromQuery || null;
  const backToChannelInfoPath = sourceChannelId
    ? `/channel/${user_username}/${sourceChannelId}/info`
    : '/channel';

  const [interactionOption, setInteractionOption] = useState('public');
  const [editDraft, setEditDraft] = useState(DEFAULT_DRAFT);
  const [loadingChannel, setLoadingChannel] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const [showStickersPopup, setShowStickersPopup] = useState(false);
  const [showAllStickersView, setShowAllStickersView] = useState(false);
  const [stickers, setStickers] = useState([]);

  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [openCoverCrop, setOpenCoverCrop] = useState(false);
  const [selectedCoverFile, setSelectedCoverFile] = useState(null);
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);
  const [isSupportActive, setIsSupportActive] = useState(false);
  const [channelSupportSelection, setChannelSupportSelection] = useState('not_active');

  useEffect(() => {
    document.body.classList.add('chat-page', 'channel-settings-view');
    return () => {
      document.body.classList.remove('chat-page', 'channel-settings-view');
    };
  }, []);

  useEffect(() => {
    const fetchStickers = async () => {
      try {
        const { data } = await UserService.getUserStickers(user_username);
        const mapped = (Array.isArray(data?.data) ? data.data : []).map((s) => ({
          _id: s._id,
          imageId: s.imageId,
          cropShape: s.cropShape,
        }));
        setStickers(mapped);
      } catch (err) {
        console.error('Failed to load stickers', err);
      }
    };

    fetchStickers();
  }, [user_username]);

  useEffect(() => {
    const fetchChannel = async () => {
      if (!sourceChannelId) {
        setEditDraft((prev) => ({ ...prev, title: user_username || '' }));
        setInteractionOption('public');
        setIsPublished(false);
        setIsSupportActive(false);
        setChannelSupportSelection('not_active');
        return;
      }

      setLoadingChannel(true);
      try {
        const { response, data } = await ChannelsService.getOne(sourceChannelId);
        if (!response?.ok) return;

        const channel = data?.data && !Array.isArray(data.data) ? data.data : data;
        const nextType = String(channel?.type || 'public');

        setEditDraft({
          title: channel?.title || '',
          type: nextType,
          description: channel?.description || '',
          image: normalizeCoverImageValue(channel?.image),
          price: channel?.price ?? '',
          currency: resolveChannelCurrency(channel?.currency),
          write_perms: normalizeWritePerms(channel?.write_perms),
        });
        setInteractionOption(nextType);
        setIsPublished(typeof channel?.is_published === 'boolean' ? channel.is_published : true);
        const ownerId = channel?.owner?._id || channel?.owner || null;
        let supportIsActive = false;
        if (ownerId) {
          try {
            const { response: supportResponse, data: supportData } = await SupportService.getConfiguration(ownerId);
            if (supportResponse?.ok) {
              supportIsActive = Boolean(supportData?.support_enabled ?? supportData?.enabled);
            }
          } catch (_) {
            supportIsActive = false;
          }
        }
        setIsSupportActive(supportIsActive);
        setChannelSupportSelection(resolveSupportSelection(channel?.channel_support_enabled, supportIsActive));
        setCoverPreviewUrl(null);
      } catch (err) {
        console.error('Failed to load channel details', err);
      } finally {
        setLoadingChannel(false);
      }
    };

    fetchChannel();
  }, [sourceChannelId, user_username]);

  const handleConfirmSticker = async (stickerData) => {
    try {
      const blob = stickerData.croppedSticker;
      const file = new File([blob], 'sticker.png', { type: blob.type || 'image/png' });
      const { response, data: uploadData } = await UserService.uploadImage(file);
      if (!response?.ok) throw new Error('Image upload failed');

      const { data } = await UserService.createUserSticker(user_username, {
        imageId: uploadData.image_id,
        cropShape: stickerData.cropShape,
      });

      setStickers((prev) => [
        ...prev,
        { _id: data._id, imageId: data.imageId, cropShape: data.cropShape },
      ]);
    } catch (err) {
      console.error('Failed to save sticker', err);
    }
    setShowStickersPopup(false);
  };

  async function handleCoverCropped(croppedImageFile) {
    try {
      setOpenCoverCrop(false);
      const blobUrl = URL.createObjectURL(croppedImageFile);
      setCoverPreviewUrl(blobUrl);
      setCoverUploading(true);

      const { response, data } = await UserService.uploadImage(croppedImageFile);
      if (response?.ok && data?.image_id) {
        setEditDraft((prev) => ({ ...prev, image: data.image_id }));
      } else {
        alert('Error uploading cover image. Please try again.');
      }
    } finally {
      setCoverUploading(false);
    }
  }

  const handleSave = async () => {
    if (!sourceChannelId) {
      navigate(backToChannelInfoPath);
      return;
    }

    setSaving(true);
    try {
      const shouldPublish = !isPublished;
      const payload = {
        title: (editDraft.title || '').trim(),
        type: editDraft.type,
        description: editDraft.description,
        image: editDraft.image || '',
        write_perms: editDraft.write_perms,
        channel_support_enabled: resolveSupportPayloadValue(channelSupportSelection, isSupportActive),
        ...(shouldPublish ? { is_published: true } : {}),
        ...(editDraft.type === 'subscribers' ? { price: Number(editDraft.price) } : { price: undefined }),
      };

      const { response } = await ChannelsService.update(sourceChannelId, payload);
      if (response?.ok) {
        if (shouldPublish) {
          setIsPublished(true);
        }
        navigate(backToChannelInfoPath, { replace: true });
      }
    } finally {
      setSaving(false);
    }
  };

  const previewBannerSrc = useMemo(() => {
    if (coverPreviewUrl) return coverPreviewUrl;
    if (!editDraft.image) return null;
    if (
      typeof editDraft.image === 'string'
      && (editDraft.image.startsWith('http://') || editDraft.image.startsWith('https://') || editDraft.image.startsWith('blob:'))
    ) {
      return editDraft.image;
    }
    return `${config.app.imageServiceDomain}/${editDraft.image}/w=1600`;
  }, [coverPreviewUrl, editDraft.image]);

  const previewIsPaid = useMemo(
    () => editDraft.type === 'subscribers' && Number(editDraft.price) > 0,
    [editDraft.type, editDraft.price]
  );

  const previewCtaLabel = useMemo(
    () => {
      if (!previewIsPaid) return 'Join Channel';

      const formattedMonthlyPrice = formatCurrencyAmount(editDraft.price, editDraft.currency || DEFAULT_CHANNEL_CURRENCY, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      return formattedMonthlyPrice
        ? `Subscribe for ${formattedMonthlyPrice}/month`
        : 'Subscribe';
    },
    [previewIsPaid, editDraft.price, editDraft.currency]
  );

  return (
    <div className="channel-settings-view">
      <header className="channel-settings-view__header">
        <BackArrowIcon
          className="channel-settings-view__back-button"
          onClick={() => navigate(backToChannelInfoPath)}
        />
        <div className="channel-settings-view__header-title">
          Channel Settings
        </div>
        <button
          type="button"
          className="channel-settings-view__preview-action"
          onClick={() => setShowPreviewPopup(true)}
        >
          <span className="channel-settings-view__preview-text">Preview</span>
          <PasswordEyeOpen className="channel-settings-view__preview-icon" />
        </button>
      </header>

      <div className="channel-settings-view__content">
        <form
          className="channel-settings-view__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="channel-settings-view__section channel-settings-view__edit-fields">
            <ImageUploading
              maxNumber={1}
              onChange={(images) => {
                if (images?.[0]?.file) {
                  setSelectedCoverFile(images[0].file);
                  setOpenCoverCrop(true);
                }
              }}
              onError={() => {}}
              maxFileSize={15000000}
            >
              {({ onImageUpload }) => (
                <div className="channel-settings-view__cover">
                  <div className="channel-settings-view__cover-header">
                    <span>Channel cover (16:9)</span>
                    {Boolean(coverPreviewUrl || editDraft.image) && (
                      <button
                        type="button"
                        className="channel-settings-view__cover-remove"
                        onClick={() => {
                          setCoverPreviewUrl(null);
                          setSelectedCoverFile(null);
                          setOpenCoverCrop(false);
                          setEditDraft((prev) => ({ ...prev, image: '' }));
                        }}
                        aria-label="Remove cover"
                        title="Remove cover"
                      >
                        <DeleteIcon className="channel-settings-view__cover-remove-icon" />
                      </button>
                    )}
                  </div>
                  <div
                    className="channel-settings-view__cover-upload-trigger"
                    role="button"
                    tabIndex={0}
                    onClick={onImageUpload}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onImageUpload();
                      }
                    }}
                  >
                    {coverPreviewUrl ? (
                      <UserProfileImage
                        className="channel-settings-view__cover-preview"
                        image={coverPreviewUrl}
                        loading={coverUploading}
                        typeOfProfile="gallery"
                        width={640}
                        height={360}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (editDraft.image ? (
                      <UserProfileImage
                        className="channel-settings-view__cover-preview"
                        image={resolveCoverImageSrc(editDraft.image)}
                        typeOfProfile="gallery"
                        width={640}
                        height={360}
                        style={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <div className="user-profile-image-generic gallery channel-settings-view__cover-preview channel-settings-view__cover-preview--empty">
                        <span className="channel-settings-view__cover-preview-empty-text">Add cover</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </ImageUploading>

            {openCoverCrop && selectedCoverFile && (
              <PopupUpdateArticleCover
                open={openCoverCrop}
                image={selectedCoverFile}
                exitCrop={() => setOpenCoverCrop(false)}
                setCroppedFile={handleCoverCropped}
                setCroppedUrl={setCoverPreviewUrl}
                headerText="Update Channel Cover"
                instructionsText="Adjust and crop your channel cover"
              />
            )}

            <PublicFormInput
              id="channelName"
              placeholder="Channel title"
              value={editDraft.title}
              onChange={(e) => {
                setEditDraft((prev) => ({ ...prev, title: e.target.value }));
              }}
            />

            <PublicFormInput
              type="select"
              id="channelType"
              placeholder="Type"
              value={interactionOption}
              selectOptions={{
                type: 'normal',
                options: ['public', 'followers', 'subscribers', 'collectors'],
              }}
              onChange={(value) => {
                setInteractionOption(value);
                setEditDraft((prev) => ({ ...prev, type: value || 'public' }));
              }}
            />

            {editDraft.type === 'subscribers' && (
              <div className="channel-settings-view__price-row">
                <PublicFormInput
                  className="channel-settings-view__price-currency"
                  type="select"
                  element="channelCurrency"
                  placeholder="Currency"
                  selectOptions={{ options: CHANNEL_CURRENCY_OPTIONS }}
                  value={editDraft.currency}
                  onChange={(value) => setEditDraft((prev) => ({ ...prev, currency: value }))}
                />
                <PublicFormInput
                  className="channel-settings-view__price-value"
                  type="number"
                  id="channelPrice"
                  placeholder="Monthly price"
                  min={0}
                  step="0.01"
                  value={editDraft.price}
                  onChange={(e) => setEditDraft((prev) => ({ ...prev, price: e.target.value }))}
                />
              </div>
            )}

            <PublicFormInput
              type="select"
              id="channelWritePerms"
              placeholder="Who can write"
              value={editDraft.write_perms}
              selectOptions={{ type: 'normal', options: WRITE_PERMISSION_OPTIONS }}
              onChange={(val) => setEditDraft((prev) => ({ ...prev, write_perms: val }))}
            />

            <PublicFormInput
              type="select"
              id="channelSupportEnabled"
              placeholder="Enable support?"
              value={channelSupportSelection}
              readOnly={!isSupportActive}
              selectOptions={{
                type: 'normal',
                options: isSupportActive ? SUPPORT_OPTIONS_ACTIVE : SUPPORT_OPTIONS_INACTIVE,
              }}
              onChange={(value) => setChannelSupportSelection(value)}
            />

            <PublicFormInput
              type="textarea"
              id="channelDescription"
              placeholder="Description (optional)"
              value={editDraft.description}
              onChange={(e) => {
                setEditDraft((prev) => ({ ...prev, description: e.target.value }));
              }}
              autoResize
              maxRows={4}
            />

            <div className="channel-settings-view__stickers-section">
              <div className="channel-settings-view__stickers-header">
                <div className="channel-settings-view__stickers-title">My Stickers</div>
                <span
                  className="channel-settings-view__stickers-see-all"
                  onClick={() => setShowAllStickersView(true)}
                >
                  See all
                </span>
              </div>
              <div className="channel-settings-view__stickers-container">
                <div className="channel-settings-view__stickers-button-container">
                  <CreateStickersButton onClick={() => setShowStickersPopup(true)} />
                </div>
                {stickers.length > 0 && (
                  <div className="channel-settings-view__stickers-preview">
                    <FHSlider
                      speed={1.4}
                      className="channel-settings-view__stickers-slider"
                      customPrevButton={<PrevButton />}
                      customNextButton={<NextButton />}
                    >
                      {stickers.map((sticker, index) => (
                        <div key={index} className="channel-settings-view__sticker-card">
                          <ArtworkImage
                            image={sticker.imageId}
                            imageClassName="channel-settings-view__sticker-image"
                            imageTemplateClassName="channel-settings-view__sticker-image"
                            style={{ borderRadius: sticker.cropShape === 'circle' ? '50%' : '0' }}
                          />
                        </div>
                      ))}
                    </FHSlider>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="channel-settings-view__actions">
            <ContinueButton type="submit" className="continue-button_two" disabled={loadingChannel || saving}>
              {saving ? (isPublished ? 'Saving...' : 'Publishing...') : (isPublished ? 'Save' : 'Publish')}
            </ContinueButton>
          </div>
        </form>
      </div>

      <CreateStickersPopup
        open={showStickersPopup}
        close={() => setShowStickersPopup(false)}
        onConfirmSticker={handleConfirmSticker}
      />

      {showAllStickersView && (
        <AllStickersView
          open={showAllStickersView}
          close={() => setShowAllStickersView(false)}
          stickers={stickers}
          onDeleteSticker={(newStickers) => setStickers(newStickers)}
        />
      )}

      <GenericPopup
        open={showPreviewPopup}
        onConfirm={() => setShowPreviewPopup(false)}
        confirmButtonText={previewCtaLabel}
        opacity
        className="generic-popup_content subscription-preview-popup"
      >
        <div className="subscription-preview-popup__header element-non-selectable">
          <h3 className="subscription-preview-popup__title">Join Channel</h3>
          <XIcon
            className="subscription-preview-popup__close"
            onClick={() => setShowPreviewPopup(false)}
          />
        </div>
        <div className="subscription-preview__content">
          <div className="subscription-preview__banner-wrapper">
            <div className="subscription-preview__banner-container">
              {previewBannerSrc ? (
                <img
                  className="subscription-preview__banner-image"
                  src={previewBannerSrc}
                  alt="Channel cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              ) : (
                <div className="subscription-preview__banner-placeholder" />
              )}
            </div>
          </div>

          <div className="subscription-preview__title channel-subscription__title">
            {editDraft.title || user_username}
          </div>

          <div className="subscription-preview__description">
            {editDraft.description || ''}
          </div>

          <div className="subscription-preview__includes-label">{t('subscription_preview.unique_benefits_label')}</div>
          <div className="subscription-preview__benefits-container">
            <ul className="subscription-preview__benefits-list">
              {editDraft.write_perms === 'none'
                ? <li>Only {user_username} can write in the channel</li>
                : <li>Interact in {user_username}&apos;s channel</li>}
              <li>Early 72h access to {user_username}&apos;s artworks</li>
              <li>AI access for {user_username}&apos;s profile and artworks</li>
            </ul>
          </div>
        </div>
      </GenericPopup>
    </div>
  );
}
